import React from "react";
import styled from "styled-components";
import { func, object, string } from "prop-types";
import Button from "../atoms/button";
import Input from "../atoms/input";
import inputValidator from "../atoms/input-validator";
import Recaptcha from "react-google-recaptcha/lib/recaptcha";

const Box = styled.div`
  display: flex;
`;

const Vertical = styled(Box)`
  flex-direction: column;
`;

const ModalWrapper = styled(Box)`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  max-width: 450px;
  justify-content: center;
  align-items: center;
  z-index: 400;
  @media (max-width: 450px) {
    left: 0;
  }
`;

const Container = styled(Vertical)`
  padding: 20px;
  padding-top: 30px;
  height: 100%;
  background: white;
  box-shadow: -4px 0 10px 0 rgba(0, 0, 0, 0.15);
  overflow: auto;

  && {
    box-sizing: border-box;
  }

  > * {
    flex-shrink: 0;
  }
`;

const Title = styled.div`
  margin-bottom: 10px;
  color: #333333;
  font-size: 16px;
  font-weight: bold;
  line-height: 20px;
`;

const TitleGroup = styled(Vertical)``;

const Message = styled.textarea`
  padding: 7px 10px;
  font-size: 14px;
  border: 1px solid #e7e6e6;
  resize: none;
`;

const Label = styled.label`
  margin-top: 10px;
  color: #333333;
  font-size: 12px;
  line-height: 20px;
`;

const ButtonsGroup = styled(Box)`
  justify-content: space-between;
`;

const Required = styled.span`
  color: red;
`;

const Captcha = styled(Box)`
  margin: 20px 0;
`;

const Notice = styled.div`
  font-size: 12px;
  line-height: 14px;
  color: #626469;
  margin-bottom: 5px;
`;

const RecaptchaContainer = ({ onChange }) => (
  <Recaptcha
    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
    onChange={onChange}
    onExpired={onChange}
  />
);

class EmailFormModal extends React.Component {
  static propTypes = {
    onClose: func,
    onSubmit: func,
    i10n: object,
    senderEmail: string,
    senderName: string
  };

  constructor(props) {
    super(props);
    this.state = {
      name: props.senderName || '',
      email: props.senderEmail || '',
      recipientName: '',
      recipientEmail: '',
      message: '',
      recaptcha: null
    };
  }



  handleInputValue = (event) => {
    const { name, value } = event.target;
    if (this.state[name] !== value) {
      this.setState({ [name]: value });
    } else {
      return false;
    }
  }

  handleTextareaValue = event => {
    return this.handleInputValue(event);
  }

  submitForm = () => {
    this.props.onSubmit({
      senderName: this.state.name,
      senderEmail: this.state.email,
      recipientName: this.state.recipientName,
      recipientEmail: this.state.recipientEmail,
      message: this.state.message,
      captchaResponse: this.state.recaptcha
    });
  }

  withRequired = label => (
    <Box>
      {label}
      <Required>*</Required>
    </Box>
  )

  isFormValid = () => {
    return inputValidator().validateFields([
      [this.state.name, inputValidator().validateFieldName],
      [this.state.email, inputValidator().validateFieldEmail],
      [this.state.recipientName, inputValidator().validateFieldName],
      [this.state.recipientEmail, inputValidator().validateFieldEmail],
    ]);
  }

  render() {
    const { i10n } = this.props;

    return (
      <ModalWrapper>
        <Container>
          <TitleGroup>
            <Title>{i10n.get('save_and_share_page_send_by_email')}</Title>
            <Notice>{i10n.get('save_and_share_page_form_notice')}</Notice>
          </TitleGroup>
          <Input
            type="text"
            required
            label={this.withRequired(i10n.get('save_configuration.name.label'))}
            name="name"
            value={this.state.name}
            onChange={this.handleInputValue}
            isValid={inputValidator().validateFieldName(this.state.name)}
            autoComplete="given-name"
          />
          <Input
            type="email"
            required
            label={this.withRequired(i10n.get('save_configuration.email.label'))}
            name="email"
            value={this.state.email}
            onChange={this.handleInputValue}
            isValid={inputValidator().validateFieldEmail(this.state.email)}
            autoComplete="email"
          />
          <Input
            type="text"
            required
            label={this.withRequired(i10n.get('save_configuration.recipient_name.label'))}
            name="recipientName"
            value={this.state.recipientName}
            onChange={this.handleInputValue}
            isValid={inputValidator().validateFieldName(this.state.recipientName)}
            autoComplete="off"
          />
          <Input
            type="email"
            required
            label={this.withRequired(i10n.get('save_configuration.recipient_email.label'))}
            name="recipientEmail"
            value={this.state.recipientEmail}
            onChange={this.handleInputValue}
            isValid={inputValidator().validateFieldEmail(this.state.recipientEmail)}
            autoComplete="off"
          />
          <Label>{i10n.get('save_configuration.mail.message.label')}</Label>
          <Message
            rows={6}
            name="message"
            onChange={this.handleTextareaValue}
            value={this.state.message}
          />
          <Captcha>
            <RecaptchaContainer
              onChange={value => this.handleInputValue({ target: { name: "recaptcha", value } })}
            />
          </Captcha>
          <ButtonsGroup>
            <Button onClick={this.props.onClose}>
              {i10n.get('app.action.cancel')}
            </Button>
            <Button
              importance="emphasized"
              disabled={!this.isFormValid() || !this.state.recaptcha}
              onClick={this.submitForm}
            >
              {i10n.get('app.action.send')}
            </Button>
          </ButtonsGroup>
        </Container>
      </ModalWrapper>
    );
  }
}

export default EmailFormModal;
